/**
 * Camp Title Search
 * Search for camps by title in Algolia
 */

import { getIndex } from "./client.js";
import {
  escapeAlgoliaFilterValue,
  normalizeTitleSearchQuery,
} from "../utils/string-utils.js";
import { buildFilters } from "../filters/filter-builder.js";
import {
  getDateRangeTimestamps,
  campMatchesDateFilter,
} from "../filters/date-filter.js";
import { state } from "../core/state.js";

/** Minimum alphanumeric characters required to trigger camp title search */
const MIN_MEANINGFUL_CHARS = 4;

/**
 * Count meaningful characters (letters and digits only, ignoring spaces/commas/special chars)
 * @param {string} str
 * @returns {number}
 */
function countMeaningfulChars(str) {
  return (str.match(/[a-zA-Z0-9]/g) || []).length;
}

/**
 * Sort hits by distance (if user coords available) then by tier
 * @param {Array} hits
 * @param {Object|null} userCoords - { lat, lng } or null
 * @returns {Array}
 */
function sortByDistanceAndTier(hits, userCoords) {
  return hits.sort((a, b) => {
    // 1. Distance sort (if user coords available)
    if (userCoords?.lat && userCoords?.lng) {
      const geoA = a._geoloc?.[0] || a._geoloc || null;
      const geoB = b._geoloc?.[0] || b._geoloc || null;

      if (geoA?.lat && geoA?.lng && geoB?.lat && geoB?.lng) {
        // Haversine distance calculation
        const R = 6371;
        const dLatA = ((geoA.lat - userCoords.lat) * Math.PI) / 180;
        const dLngA = ((geoA.lng - userCoords.lng) * Math.PI) / 180;
        const aA =
          Math.sin(dLatA / 2) ** 2 +
          Math.cos((userCoords.lat * Math.PI) / 180) *
            Math.cos((geoA.lat * Math.PI) / 180) *
            Math.sin(dLngA / 2) ** 2;
        const distA = R * 2 * Math.atan2(Math.sqrt(aA), Math.sqrt(1 - aA));

        const dLatB = ((geoB.lat - userCoords.lat) * Math.PI) / 180;
        const dLngB = ((geoB.lng - userCoords.lng) * Math.PI) / 180;
        const aB =
          Math.sin(dLatB / 2) ** 2 +
          Math.cos((userCoords.lat * Math.PI) / 180) *
            Math.cos((geoB.lat * Math.PI) / 180) *
            Math.sin(dLngB / 2) ** 2;
        const distB = R * 2 * Math.atan2(Math.sqrt(aB), Math.sqrt(1 - aB));

        if (Math.abs(distA - distB) > 1) {
          return distA - distB;
        }
      }
    }

    // 2. Tier sort (fallback)
    const tierA = parseInt(a.tier, 10);
    const tierB = parseInt(b.tier, 10);
    const hasA = !isNaN(tierA);
    const hasB = !isNaN(tierB);
    if (hasA && hasB) return tierA - tierB;
    if (hasA) return -1;
    if (hasB) return 1;
    return 0;
  });
}

/**
 * Get user coordinates from state or cache
 * @param {Object} state - Search state
 * @returns {Object|null} - { lat, lng } or null
 */
function getUserCoords(state) {
  if (state?.coordinates?.lat && state?.coordinates?.lng) {
    return state.coordinates;
  }

  // Try cached location
  try {
    const cached = window.CampsUtils?.getUserCachedLocation?.(
      window.CampsUtils?.defaults?.cacheKey,
      window.CampsUtils?.defaults?.cacheDuration
    );
    if (cached?.lat && cached?.lng) {
      return { lat: cached.lat, lng: cached.lng };
    }
  } catch (e) {
    // Ignore errors
  }

  return null;
}

/**
 * Search camps by title in Algolia with optional full filters support
 * Tries exact match first, then fuzzy matching if needed
 * Only searches if input has 6+ meaningful characters (letters/digits)
 * Results are sorted by: startsWith priority, then distance, then tier
 * @param {string} title - Camp title to search for
 * @param {Array|Object} options - Either sportSlug array (legacy) or options object
 *   - sportSlug: Array of sport slugs to filter by
 *   - applyAllFilters: If true, applies all active filters (sport, brand, type, gender, age, dates)
 * @param {Object} stateOverride - Optional search state for coordinates (legacy param)
 * @returns {Promise<Object|null>} - { hits, nbHits, firstCamp } or null
 */
export async function searchCampByTitle(
  title,
  options = [],
  stateOverride = null
) {
  // Backward compatibility: if options is an array, treat it as sportSlug
  const opts = Array.isArray(options)
    ? { sportSlug: options, applyAllFilters: false }
    : {
        sportSlug: options.sportSlug || [],
        applyAllFilters: options.applyAllFilters || false,
      };

  const index = getIndex();

  if (!title || !title.trim() || !index) {
    return null;
  }

  // Only search by title if input has 6+ meaningful characters
  if (countMeaningfulChars(title) < MIN_MEANINGFUL_CHARS) {
    console.log(
      `🔍 [ALGOLIA CAMP SEARCH] Skipping title search: "${title}" has fewer than ${MIN_MEANINGFUL_CHARS} meaningful characters`
    );
    return null;
  }

  try {
    const searchQuery = title.trim();

    // Build filters based on applyAllFilters flag
    let additionalFilters;
    if (opts.applyAllFilters) {
      // Use full buildFilters() which includes sport, brand, type, gender, age, live, tier
      additionalFilters = buildFilters();
      console.log(
        "🔍 [ALGOLIA CAMP SEARCH] Searching with full filters:",
        searchQuery,
        additionalFilters || "(no filters)"
      );
    } else {
      // Legacy behavior: sport filter only
      const sportFilter =
        opts.sportSlug.length > 0
          ? opts.sportSlug.map((s) => `sportSlug:"${s}"`).join(" OR ")
          : "";
      additionalFilters = sportFilter ? `(${sportFilter})` : "";
      console.log(
        "🔍 [ALGOLIA CAMP SEARCH] Searching for camps by title:",
        searchQuery,
        opts.sportSlug.length > 0
          ? `with sport filter: ${opts.sportSlug.join(", ")}`
          : ""
      );
    }

    // Try exact match first using filter. Escape special chars so "Nike | MLB Community Camps"
    // works: pipe and quotes can break Algolia filter parsing.
    const escapedForFilter = escapeAlgoliaFilterValue(searchQuery);
    let titleFilter = `title:"${escapedForFilter}"`;
    let filters = additionalFilters
      ? `${titleFilter} AND ${additionalFilters}`
      : titleFilter;

    let searchParams = {
      filters: filters,
      hitsPerPage: 1000,
    };

    let { hits, nbHits } = await index.search("", searchParams);

    // If no exact match or very few results, try text search (fuzzy matching).
    // Normalize: replace | with space so it is not treated as separator / OR.
    if (!hits || hits.length === 0 || nbHits < 3) {
      console.log(
        "🔍 [ALGOLIA CAMP SEARCH] No exact match or few results, trying text search"
      );
      const queryForSearch = normalizeTitleSearchQuery(searchQuery);
      const textSearchParams = { hitsPerPage: 1000 };
      if (additionalFilters) {
        textSearchParams.filters = additionalFilters;
      }
      const result = await index.search(queryForSearch, textSearchParams);
      hits = result.hits;
      nbHits = result.nbHits || hits.length;
    }

    // Apply client-side date filtering when using full filters
    // (dates cannot be filtered in Algolia directly)
    if (opts.applyAllFilters && state.beginDates && state.endDates) {
      const dateRange = getDateRangeTimestamps();
      if (dateRange) {
        const beforeCount = hits.length;
        hits = hits.filter((camp) => campMatchesDateFilter(camp, dateRange));
        nbHits = hits.length;
        console.log(
          `📅 [ALGOLIA CAMP SEARCH] After date filtering: ${hits.length} camps (was ${beforeCount})`
        );
      }
    }

    if (!hits || hits.length === 0) {
      console.log(
        "❌ [ALGOLIA CAMP SEARCH] No camps found with title:",
        searchQuery
      );
      return null;
    }

    // Prioritize camps that START with query (same as autocomplete)
    const queryLower = searchQuery.toLowerCase();
    const startsWithQuery = hits.filter((h) =>
      h.title?.toLowerCase().startsWith(queryLower)
    );
    const containsQuery = hits.filter(
      (h) => !h.title?.toLowerCase().startsWith(queryLower)
    );

    // Get user coordinates for distance sorting (use stateOverride or global state)
    const userCoords = getUserCoords(stateOverride || state);

    // Sort each group by distance + tier, then combine
    hits = [
      ...sortByDistanceAndTier(startsWithQuery, userCoords),
      ...sortByDistanceAndTier(containsQuery, userCoords),
    ];

    console.log(
      `✅ [ALGOLIA CAMP SEARCH] Found ${hits.length} camps (${
        startsWithQuery.length
      } starts with, ${containsQuery.length} contains)${
        opts.applyAllFilters ? " with full filters" : ""
      }`
    );

    // Get first camp for coordinates (for fallback if needed)
    const firstCamp = hits[0];
    let lat = null;
    let lng = null;

    if (firstCamp._geoloc) {
      if (Array.isArray(firstCamp._geoloc) && firstCamp._geoloc.length > 0) {
        lat = firstCamp._geoloc[0].lat;
        lng = firstCamp._geoloc[0].lng;
      } else if (firstCamp._geoloc.lat && firstCamp._geoloc.lng) {
        lat = firstCamp._geoloc.lat;
        lng = firstCamp._geoloc.lng;
      }
    } else if (firstCamp.lat && firstCamp.lng) {
      lat = firstCamp.lat;
      lng = firstCamp.lng;
    }

    // Extract city and state from first camp address if available
    const address = firstCamp.address || {};
    const city = address.city || "";
    const campState = address.state || address.regionCode || "";

    return {
      hits: hits, // All found camps
      nbHits: nbHits, // Total count
      firstCamp:
        lat && lng
          ? {
              lat,
              lng,
              city,
              state: campState,
              camp: firstCamp,
            }
          : null,
    };
  } catch (error) {
    console.error("❌ [ALGOLIA CAMP SEARCH] Error searching for camps:", error);
    return null;
  }
}

console.log("📦 Camp title search module loaded");
